import React from 'react';
import { PageEngine } from '../page-engine/PageEngine';
import { runResumeIntelligence } from '../intelligence/resumeIntelligence';
import { resolveDocumentStrategy } from '../strategy/DocumentStrategy';
import { getTheme } from '../../themes';
import { getLayout } from '../../layouts';
import { getSectionComponent } from './SectionRegistry';
import { SectionWrapper } from './SectionWrapper';
import { validateCapabilities } from './CapabilityChecker';
import { calculateResumeMetrics } from '../../design-system/metrics/resumeMetrics';
import { getRegistryItem } from '../../components/editor/registry';
import { DESIGN_TOKENS, DENSITY_TOKENS } from '../../templates/sdk/tokens';

/**
 * ResumeRenderer acts as the main pipeline runner.
 * It combines raw resume data, target persona routing, templates, themes,
 * components, and mounts them inside the PageEngine.
 */
export const ResumeRenderer = ({
  resumeData,
  templateConfig,
  themeId,
  layoutId,
  personaId = 'engineering',
  userOverrides = {},
  targetPageCount = 1,
  showPageBoundaries = false,
  onPaginationComplete,
  onValidationWarnings
}) => {
  if (!resumeData) return null;

  // Resolve V2 manifest properties or default theme/layout fallback
  const activeThemeId = themeId || templateConfig.theme || templateConfig.defaultTheme || 'classic-navy';
  const activeLayoutId = layoutId || templateConfig.layout || templateConfig.defaultLayout || 'single-column';

  console.log(`[TRACE PIPE:template S6:ResumeRenderer] id=${templateConfig.id} theme=${activeThemeId} layout=${activeLayoutId}`);

  const selectedTheme = getTheme(activeThemeId);
  const templateLayoutConfig = templateConfig.layouts?.[activeLayoutId];

  // 2. Validate Template Capabilities
  const warnings = validateCapabilities(templateConfig, resumeData);
  if (onValidationWarnings && warnings.length > 0) {
    onValidationWarnings(warnings);
  }

  // 3. Resume Intelligence Layer (Normalizes content & groups)
  const supportsRating = templateConfig.supports?.rating || templateConfig.capabilities?.supportsRating;
  const skillsComponentType = supportsRating ? 'ratings' : 'categorized';
    
  const { processedData, activeSections } = runResumeIntelligence(resumeData, {
    skillsComponent: skillsComponentType
  });

  // Calculate dynamic metrics on initial run
  const metrics = calculateResumeMetrics(processedData, activeSections, targetPageCount, 'comfortable');
  console.log('[METRICS] Calculated resume metrics:', metrics);

  // 4. Document Strategy Layer
  const slotMappings = resolveDocumentStrategy({
    resumeData: processedData,
    activeSections,
    personaId,
    layoutId: activeLayoutId,
    templateLayoutConfig,
    templateConfig
  });

  const activeDensity = templateConfig.density || (templateConfig.metadata && templateConfig.metadata.density) || 'comfortable';
  const densityStyles = DENSITY_TOKENS[activeDensity] || DENSITY_TOKENS.comfortable;

  // 5. Compute Dynamic Theme Variable Inheritances
  const combinedStyles = {
    // ── Baseline Design System Tokens ──
    ...DESIGN_TOKENS,

    // ── Spacing Density Tokens ──
    ...densityStyles,

    // ── Font & Element Overrides fallback ──
    '--font-size-display': '24pt',
    '--font-size-heading': '11pt',
    '--font-size-subheading': '9.5pt',
    '--font-size-body': '9pt',
    '--font-size-bullet': '8.8pt',
    '--font-size-meta': '8pt',
    '--font-size-caption': '7.5pt',
    '--font-size-label': '8pt',

    '--line-height-display': '1.2',
    '--line-height-heading': '1.25',
    '--line-height-subheading': '1.3',
    '--line-height-body': '1.45',
    '--line-height-bullet': '1.35',
    '--line-height-meta': '1.2',
    '--line-height-caption': '1.2',
    '--line-height-label': '1.2',

    '--weight-display': '800',
    '--weight-heading': '800',
    '--weight-subheading': '700',
    '--weight-body': '400',
    '--weight-meta': '400',
    '--weight-caption': '400',
    '--weight-label': '500',

    '--spacing-margin': '24px',
    '--spacing-element-gap': '16px',
    '--spacing-paragraph-gap': '6px',
    '--spacing-bullet-gap': '4px',
    '--spacing-section-mb': '16px',
    '--spacing-item-mb': '12px',
    '--border-thickness-heading': '1.5px',
    '--reading-width': '72ch',

    // ── Theme Inherited Properties ──
    '--font-family-primary': selectedTheme.variables['--font-family-primary'],
    '--font-family-secondary': selectedTheme.variables['--font-family-secondary'],
    '--color-primary': selectedTheme.variables['--color-primary'],
    '--color-secondary': selectedTheme.variables['--color-secondary'],
    '--color-accent': selectedTheme.variables['--color-accent'],
    '--color-bg-page': selectedTheme.variables['--color-bg-page'],
    '--color-text-main': selectedTheme.variables['--color-text-main'],
    '--color-text-light': selectedTheme.variables['--color-text-light'],
    '--color-border': selectedTheme.variables['--color-border'],
    '--bullet-style': selectedTheme.variables['--bullet-style'],
    
    // Merge template-specific overrides
    ...templateConfig.styleOverrides,
    
    // Merge layout overrides
    ...templateLayoutConfig?.overrides,
    
    // Merge user custom overrides
    ...userOverrides
  };

  // 6. Resolve Layout Component
  const LayoutComponent = getLayout(activeLayoutId);

  // Resolve divider style from template config (defaults to 'line' — no regression)
  const dividerStyle = templateConfig.sectionDividerStyle || 'line';

  // Helper to map and render component structures into slot trees
  const renderSlotElements = (sectionIds) => {
    const componentConfig = templateConfig.components || {};
    const sectionSettings = templateConfig.sectionSettings || {};

    return sectionIds.map((secId) => {
      if (secId === 'personal-info') {
        // Render header based on template preference
        const headerStyleId = componentConfig.header || (activeThemeId === 'classic-navy' ? 'header-classic' : 'header-centered');
        const HeaderComponent = getSectionComponent(headerStyleId);
        return <HeaderComponent key="header" personalInfo={processedData.personalInfo} />;
      }

      if (secId === 'contact-sidebar') {
        const ContactSidebar = getSectionComponent('contact-sidebar');
        return <ContactSidebar key="contact-sidebar" personalInfo={processedData.personalInfo} />;
      }

      // Check registry dynamically
      const registryItem = getRegistryItem(secId);
      if (registryItem) {
        // ── Per-section configuration from template sectionSettings ──
        const secConfig = sectionSettings[secId] || {};

        // 1. Resolve renderer: sectionSettings.style > components map > registry default
        //    'inline' is a shorthand that maps to the InlineListBlock renderer
        let rendererComponentId = secConfig.style
          ? (secConfig.style === 'inline' ? 'inline-list' : secConfig.style)
          : (componentConfig[secId] || registryItem.rendererComponent);

        // Resolve component from SectionRegistry
        const Comp = getSectionComponent(rendererComponentId);
        if (!Comp) return null;

        // 2. Per-section density → scoped CSS variable overrides on SectionWrapper
        const secDensity = secConfig.density;
        let sectionStyle = {};
        if (secDensity) {
          const densitySpec = DENSITY_TOKENS[secDensity] || {};
          sectionStyle = {
            '--spacing-section-mb': densitySpec['--spacing-section-mb'],
            '--spacing-item-mb': densitySpec['--spacing-item-mb'],
            '--spacing-bullet-gap': densitySpec['--spacing-bullet-gap'],
            '--spacing-paragraph-gap': densitySpec['--spacing-paragraph-gap'],
            '--line-height-body': densitySpec['--line-height-body'],
          };
          // Remove undefined keys so we don't pollute the style object
          Object.keys(sectionStyle).forEach(k => sectionStyle[k] == null && delete sectionStyle[k]);
        }

        let items = [];
        if (secId === 'skills') {
          items = processedData.skills;
        } else if (secId === 'summary') {
          items = processedData.summary || processedData.summaryText;
        } else {
          items = processedData[secId] || processedData[`${secId}List`] || [];
        }

        // Title and layout overrides
        const displayTitle = registryItem.id.startsWith('custom') && processedData[secId]?.title
          ? processedData[secId].title
          : registryItem.title;

        const canSplit = registryItem.layout?.allowPageBreak !== false;

        return (
          <SectionWrapper key={secId} id={secId} title={displayTitle} canSplit={canSplit} dividerStyle={dividerStyle} style={sectionStyle}>
            <Comp 
              items={items} 
              categories={secId === 'skills' ? processedData.skillsCategories : undefined} 
            />
          </SectionWrapper>
        );
      }

      // Fallback renderer for unmatched items
      return (
        <SectionWrapper key={secId} id={secId} title={secId.toUpperCase()} canSplit={true} dividerStyle={dividerStyle}>
          <div style={{ fontSize: 'var(--font-size-body, 9pt)', color: 'var(--color-text-main)' }}>
            {JSON.stringify(processedData[secId])}
          </div>
        </SectionWrapper>
      );
    });
  };


  const slots = {
    header: renderSlotElements(slotMappings.header),
    main: renderSlotElements(slotMappings.main),
    sidebar: renderSlotElements(slotMappings.sidebar),
    footer: renderSlotElements(slotMappings.footer)
  };

  const handlePaginationComplete = (result) => {
    if (onPaginationComplete) {
      let finalMetrics = null;
      try {
        finalMetrics = calculateResumeMetrics(processedData, activeSections, result.pages, result.finalDensity);
      } catch (metricsErr) {
        // Metrics failure must never prevent the print-ready signal from firing.
        // The PDF renders correctly even without metrics — metrics are for the editor UI only.
        console.warn('[ResumeRenderer] calculateResumeMetrics failed:', metricsErr);
      }
      onPaginationComplete({
        ...result,
        metrics: finalMetrics,
      });
    }
  };


  return (
    <PageEngine 
      targetPageCount={targetPageCount} 
      onPaginationComplete={handlePaginationComplete} 
      showPageBoundaries={showPageBoundaries}
      resumeData={resumeData}
    >
      <div 
        className="resume-theme-injections"
        style={{
          minHeight: '100%',
          fontFamily: 'var(--font-family-primary)',
          color: 'var(--color-text-main)',
          backgroundColor: 'var(--color-bg-page)',
          padding: 'var(--spacing-margin, 24px)',
          ...combinedStyles
        }}
      >
        <LayoutComponent slots={slots} spacingMultiplier={userOverrides['--spacing-multiplier'] || 1.0} />
      </div>
    </PageEngine>
  );
};
